<template>
  <canvas ref="canvas" class="emoji-rise" aria-hidden="true"></canvas>
</template>

<script>
// Emoji "rise" — Google-Meet-style floating reactions. Emojis spawn along
// the bottom edge and drift up through the viewport with a sine sway,
// gentle scale-up and a fade-out near the top. Unlike ConfettiRain this
// runs INFINITELY by default: fire() keeps spawning until stop() is
// called (the modal that owns it decides when the party ends).
//
// Usage:
//   <EmojiRise ref="emojis" :emojis="['👏🏻']" />
//   this.$refs.emojis.fire()
//   this.$refs.emojis.stop()
//
// Honours `prefers-reduced-motion` — fire() is a no-op when the OS
// setting is on, so the modal copy still reads normally without motion.

export default {
  name: 'EmojiRise',
  props: {
    // Glyphs to rain upward. Repeat an entry to weight it higher.
    emojis: { type: Array, default: () => ['👏🏻'] },
    // Average milliseconds between spawns. Lower = denser stream.
    spawnIntervalMs: { type: Number, default: 110 },
    // Upward speed in px/frame before per-particle variance.
    riseSpeed: { type: Number, default: 1.6 },
    // Base glyph size in px; each particle varies around this.
    size: { type: Number, default: 34 },
    // Hard cap so a long-open modal can't grow the particle list forever.
    maxParticles: { type: Number, default: 90 }
  },
  data () {
    return {
      handle: null,
      particles: [],
      running: false,
      nextSpawnAt: 0,
      width: 0,
      height: 0
    }
  },
  beforeDestroy () {
    this.stop()
    window.removeEventListener('resize', this.resizeCanvas)
  },
  methods: {
    fire () {
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.getContext) return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

      this.stop()
      this.resizeCanvas()
      window.addEventListener('resize', this.resizeCanvas)
      this.running = true
      this.nextSpawnAt = 0
      // Seed a few mid-flight particles so the screen isn't empty for the
      // first second while the stream builds up from the bottom edge.
      for (let i = 0; i < 10; i++) this.spawn(Math.random())
      this.loop()
    },
    stop () {
      this.running = false
      window.removeEventListener('resize', this.resizeCanvas)
      if (this.handle) {
        window.cancelAnimationFrame(this.handle)
        this.handle = null
      }
      this.particles = []
      const canvas = this.$refs.canvas
      if (canvas && canvas.getContext) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    },
    resizeCanvas () {
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.getContext) return
      const dpr = window.devicePixelRatio || 1
      this.width = window.innerWidth
      this.height = window.innerHeight
      canvas.width = this.width * dpr
      canvas.height = this.height * dpr
      canvas.style.width = `${this.width}px`
      canvas.style.height = `${this.height}px`
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
    },
    // `progress` (0 = bottom edge, 1 = top) lets the seed pass drop
    // particles part-way up the screen instead of all at the floor.
    spawn (progress = 0) {
      if (this.particles.length >= this.maxParticles) return
      const travel = this.height + 120
      this.particles.push({
        // Bias the stream toward the screen edges so the modal panel in
        // the middle stays readable while emojis stream around it.
        x: this.edgeBiasedX(),
        y: this.height + 40 - travel * progress,
        vy: this.riseSpeed * (0.7 + Math.random() * 0.8),
        size: this.size * (0.65 + Math.random() * 0.7),
        glyph: this.emojis[Math.floor(Math.random() * this.emojis.length)],
        driftPhase: Math.random() * Math.PI * 2,
        driftSpeed: 0.02 + Math.random() * 0.03,
        driftMag: 0.6 + Math.random() * 1.6,
        opacity: 0
      })
    },
    edgeBiasedX () {
      // 80% of the stream hugs the outer thirds (left / right of the
      // panel); the rest crosses the middle so the field still feels
      // like it covers the whole screen.
      if (Math.random() < 0.8) {
        const band = 0.02 + Math.random() * 0.32
        return Math.random() < 0.5 ? this.width * band : this.width * (1 - band)
      }
      return this.width * (0.34 + Math.random() * 0.32)
    },
    loop () {
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.getContext) return
      const ctx = canvas.getContext('2d')

      const tick = () => {
        ctx.clearRect(0, 0, this.width, this.height)

        const now = performance.now()
        if (this.running && now >= this.nextSpawnAt) {
          this.spawn()
          // Jitter the cadence so the stream never looks metronomic.
          this.nextSpawnAt = now + this.spawnIntervalMs * (0.6 + Math.random() * 0.9)
        }

        const alive = []
        for (let i = 0; i < this.particles.length; i++) {
          const p = this.particles[i]
          p.driftPhase += p.driftSpeed
          p.y -= p.vy
          p.x += Math.sin(p.driftPhase) * p.driftMag
          if (p.y < -80) continue

          // Fade in off the bottom edge, fade out into the top quarter.
          const fadeIn = Math.min(1, (this.height + 40 - p.y) / 90)
          const fadeOut = Math.min(1, Math.max(0, p.y / (this.height * 0.28)))
          p.opacity = Math.min(fadeIn, fadeOut) * 0.95

          alive.push(p)
          ctx.save()
          ctx.globalAlpha = p.opacity
          // Never rotated — the emoji always renders upright, exactly as
          // the glyph is drawn.
          ctx.translate(p.x, p.y)
          ctx.font = `${p.size}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(p.glyph, 0, 0)
          ctx.restore()
        }
        this.particles = alive

        if (this.running || alive.length > 0) {
          this.handle = window.requestAnimationFrame(tick)
        } else {
          this.handle = null
          ctx.clearRect(0, 0, this.width, this.height)
        }
      }
      this.handle = window.requestAnimationFrame(tick)
    }
  }
}
</script>

<style scoped>
.emoji-rise {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1300;
}
</style>
